import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Package } from 'lucide-react';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ActionButton } from '@/shared/ui/action-button';

import { PalletView } from '@/features/pallet-view';
import { ConsignmentLineDetailsFormModal } from '@/features/consignment-line-details-modal';
import { CreatePalletModal } from '@/features/create-pallet-modal';

import { usePermissions } from '@/shared/lib';
import { CONSIGNMENT_STATUS, type ConsignmentStatusCode, type PalletPortion } from '@/entities/consignments';

interface ConsignmentLinesCardProps {
  consignmentId: string;
  consignmentStatus?: ConsignmentStatusCode;
  isReadOnly?: boolean;
}

export function ConsignmentLinesCard({
  consignmentId,
  consignmentStatus = CONSIGNMENT_STATUS.NR,
  isReadOnly = false
}: ConsignmentLinesCardProps) {
  const { t } = useTranslation();
  const { useConsignmentLinePermissions } = usePermissions();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreatePalletModalOpen, setIsCreatePalletModalOpen] = useState(false);
  const [selectedPortions, setSelectedPortions] = useState<PalletPortion[]>([]);

  const linePermissions = useConsignmentLinePermissions(isReadOnly, consignmentStatus);
  const canAddLine = linePermissions.canAddLine;

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
  };

  return (
    <>
      <Card className="md:col-span-3">
        <CardHeader className="flex flex-row items-center justify-between">
          {canAddLine && (
            <div className="flex items-center gap-2 w-full">
              <ActionButton
                type="button"
                size="sm"
                intent="action"
                className="gap-2 flex-1"
                onClick={() => setIsCreateModalOpen(true)}>
                <Plus className="h-4 w-4" />
                {t('newcmr.createline.label')}
              </ActionButton>
              <ActionButton
                type="button"
                size="sm"
                intent="action"
                className="gap-2 flex-1"
                onClick={() => setIsCreatePalletModalOpen(true)}>
                <Package className="h-4 w-4" />
                {t('tracking.servicereq.addOverpacking.label')}
              </ActionButton>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <PalletView
            consignmentId={consignmentId}
            consignmentStatus={consignmentStatus}
            isReadOnly={isReadOnly}
            selectedPortions={selectedPortions}
            onSelectionChange={setSelectedPortions}
          />
        </CardContent>
      </Card>

      <ConsignmentLineDetailsFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
        consignmentId={consignmentId}
        mode="create"
      />

      <CreatePalletModal
        isOpen={isCreatePalletModalOpen}
        onClose={() => setIsCreatePalletModalOpen(false)}
        consignmentId={consignmentId}
        selectedPortions={selectedPortions}
      />
    </>
  );
}
